/* Rounded-edge backdrop refraction from the HTML reference. No foreground filtering. */
(() => {
  if (!navigator.userAgentData?.brands?.some(({ brand }) => brand === 'Chromium')) return;
  if (!CSS.supports('backdrop-filter', 'url(#glass)')) return;
  const ns = 'http://www.w3.org/2000/svg';
  const make = (tag, attrs = {}) => {
    const node = document.createElementNS(ns, tag);
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
    return node;
  };
  const svg = make('svg', { width: 0, height: 0, 'aria-hidden': 'true', focusable: 'false' });
  svg.classList.add('glass-filter-definitions');
  document.body.append(svg);
  const surfaces = new Map();
  let serial = 0;
  const paint = (surface) => {
    const record = surfaces.get(surface);
    if (!record || !surface.checkVisibility() || surface.closest('.navigation-dialog')) return;
    const w = Math.max(1, Math.round(surface.clientWidth / 3));
    const h = Math.max(1, Math.round(surface.clientHeight / 3));
    const radius = (parseFloat(getComputedStyle(surface).borderRadius) || 6) / 3;
    const material = getComputedStyle(surface);
    record.filter
      .querySelector('feGaussianBlur')
      .setAttribute(
        'stdDeviation',
        parseFloat(material.getPropertyValue('--glass-dark-blur')) || 18,
      );
    record.filter
      .querySelector('feColorMatrix')
      .setAttribute('values', material.getPropertyValue('--glass-dark-saturation').trim() || '.8');
    const key = `${w}:${h}:${radius}`;
    if (record.key === key) return;
    record.key = key;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const context = canvas.getContext('2d');
    if (!context) return;
    const image = context.createImageData(w, h);
    const r = Math.min(radius, w / 2, h / 2);
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) {
        const cx = x + 0.5 - w / 2,
          cy = y + 0.5 - h / 2;
        const px = Math.abs(cx) - (w / 2 - r),
          py = Math.abs(cy) - (h / 2 - r);
        const ox = Math.max(px, 0),
          oy = Math.max(py, 0);
        const distance = Math.hypot(ox, oy) + Math.min(Math.max(px, py), 0) - r;
        const k = 1 - Math.max(0, Math.min(1, -distance / 14));
        const bend = k * k * (3 - 2 * k);
        let nx = 0,
          ny = 0;
        if (px > 0 && py > 0) {
          const length = Math.hypot(ox, oy) || 1;
          nx = (Math.sign(cx) * ox) / length;
          ny = (Math.sign(cy) * oy) / length;
        } else if (px > py) nx = Math.sign(cx);
        else ny = Math.sign(cy);
        const i = (y * w + x) * 4;
        image.data[i] = 128 + nx * bend * 127;
        image.data[i + 1] = 128 + ny * bend * 127;
        image.data[i + 2] = 128;
        image.data[i + 3] = 255;
      }
    context.putImageData(image, 0, 0);
    record.map.setAttribute('href', canvas.toDataURL());
    surface.style.setProperty('--glass-refraction', `url("#${record.filter.id}")`);
    surface.setAttribute('data-glass-refraction', '');
  };
  const observer = new ResizeObserver((entries) => entries.forEach(({ target }) => paint(target)));
  const connect = (root) =>
    root.querySelectorAll('.glass-surface').forEach((surface) => {
      if (surfaces.has(surface)) return;
      const filter = make('filter', {
        id: `C4Glass-${++serial}`,
        x: 0,
        y: 0,
        width: '100%',
        height: '100%',
        'color-interpolation-filters': 'sRGB',
      });
      const map = make('feImage', {
        result: 'map',
        width: '100%',
        height: '100%',
        preserveAspectRatio: 'none',
      });
      filter.append(
        map,
        make('feDisplacementMap', {
          in: 'SourceGraphic',
          in2: 'map',
          scale: 26,
          xChannelSelector: 'R',
          yChannelSelector: 'G',
        }),
        make('feGaussianBlur', { stdDeviation: 18 }),
        make('feColorMatrix', { type: 'saturate', values: '.8' }),
      );
      svg.append(filter);
      surfaces.set(surface, { filter, map });
      observer.observe(surface);
    });
  connect(document);
  document.addEventListener('shopify:section:load', ({ target }) => connect(target));
  document.addEventListener('shopify:section:unload', ({ target }) => {
    for (const [surface, record] of surfaces)
      if (target.contains(surface)) {
        observer.unobserve(surface);
        record.filter.remove();
        surfaces.delete(surface);
      }
  });
  document.addEventListener(
    'toggle',
    ({ target }) => {
      if (target.open)
        requestAnimationFrame(() => {
          if (target.matches('.glass-surface')) paint(target);
          target.querySelectorAll('.glass-surface').forEach(paint);
        });
    },
    true,
  );
})();
